import { createContext, useState, useMemo, useContext, useCallback } from 'react';
import { useGeneral } from '../../Context/GeneralContext';

const CalidadContext = createContext();

const CATEGORIAS = [
    { key: 'DefectosCalidad', label: 'Tipos de defecto' },
    { key: 'CausaRaizCalidad', label: 'Causas raíz' },
];

export function CalidadProvider(props) {
    const { tokenR } = useGeneral();
    const [categoria, setCategoria] = useState(null);
    const [items, setItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [nuevo, setNuevo] = useState(false);
    const [editar, setEditar] = useState(false);
    const [snackbar, setSnackbar] = useState(false);
    const [snackbarText, setSnackbarText] = useState('');
    const [errorMsg, setErrorMsg] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const getHeaders = () => ({ authorization: `Bearer ${tokenR.current}`, Accept: 'application/json' });

    const getItems = useCallback(async (cat) => {
        setLoading(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/MicroServiciosController?categoria=${cat}`,
                { headers: getHeaders() }
            );
            if (!res.ok) return;
            const obj = await res.json();
            obj.forEach(item => { item.id = item.codigo; });
            setItems(obj);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenR]);

    const selectCategoria = useCallback((cat) => {
        setCategoria(cat);
        setSelectedItem(null);
        setNuevo(false);
        setEditar(false);
        setErrorMsg(null);
        getItems(cat);
    }, [getItems]);

    const postItem = useCallback(async (nombre) => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/MicroServiciosController`,
                {
                    method: 'POST',
                    body: JSON.stringify({ categoria, nombre }),
                    headers: { ...getHeaders(), 'Content-Type': 'application/json' },
                }
            );
            if (!res.ok) { setErrorMsg('Error al guardar. Intenta de nuevo.'); return false; }
            const label = CATEGORIAS.find(c => c.key === categoria)?.label || '';
            setSnackbarText(`${label} creado correctamente`);
            setSnackbar(true);
            setNuevo(false);
            getItems(categoria);
            return true;
        } catch {
            setErrorMsg('Error al guardar. Intenta de nuevo.');
            return false;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenR, getItems, categoria]);

    const putItem = useCallback(async (codigo, nombre) => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/MicroServiciosController/${categoria}/${codigo}`,
                {
                    method: 'PUT',
                    body: JSON.stringify({ nombre }),
                    headers: { ...getHeaders(), 'Content-Type': 'application/json' },
                }
            );
            if (!res.ok) { setErrorMsg('Error al guardar. Intenta de nuevo.'); return false; }
            const label = CATEGORIAS.find(c => c.key === categoria)?.label || '';
            setSnackbarText(`${label} actualizado correctamente`);
            setSnackbar(true);
            setEditar(false);
            setSelectedItem(null);
            getItems(categoria);
            return true;
        } catch {
            setErrorMsg('Error al guardar. Intenta de nuevo.');
            return false;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenR, getItems, categoria]);

    const deleteItem = useCallback(async (codigo) => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/MicroServiciosController/${categoria}/${codigo}`,
                { method: 'DELETE', headers: getHeaders() }
            );
            if (!res.ok) { setSnackbarText('Error al eliminar'); setSnackbar(true); return; }
            const label = CATEGORIAS.find(c => c.key === categoria)?.label || '';
            setSnackbarText(`${label} eliminado correctamente`);
            setSnackbar(true);
            setDeleteConfirm(null);
            getItems(categoria);
        } catch {
            setSnackbarText('Error al eliminar');
            setSnackbar(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenR, getItems, categoria]);

    const value = useMemo(() => ({
        CATEGORIAS,
        categoria, selectCategoria,
        items, getItems, loading,
        selectedItem, setSelectedItem,
        nuevo, setNuevo,
        editar, setEditar,
        snackbar, setSnackbar, snackbarText,
        errorMsg, setErrorMsg,
        deleteConfirm, setDeleteConfirm,
        postItem, putItem, deleteItem,
    }), [
        categoria, selectCategoria,
        items, getItems, loading,
        selectedItem,
        nuevo, editar,
        snackbar, snackbarText,
        errorMsg,
        deleteConfirm,
        postItem, putItem, deleteItem,
    ]);

    return <CalidadContext.Provider value={value} {...props} />;
}

export function useCalidad() {
    const context = useContext(CalidadContext);
    if (!context) throw new Error('useCalidad debe estar dentro del proveedor CalidadContext');
    return context;
}
